// Редовете на справката „Наличности" — типът, сметките (нето/бруто по ДДС
// регистрацията), търсенето, филтрите, сортирането и KPI-тата. Стоят отделно
// от страницата, за да остане тя само изглед (както при „Доставки").
import type { Product, StockBalance } from "../../../../api/inventory";
import { isDeletedProduct } from "../../components/product/deletedProducts";
import { clampDecimals, formatQty } from "../../components/qty";
import { shownStock, soldWithoutStockOf } from "../../components/negativeStock";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";
import {
  compareNumber,
  compareText,
  matchesWords,
  searchWords,
  sortRows,
  type ReportSort,
} from "./reportListModel";

export type StockBucket = "under" | "near" | "over";

export type StockRow = {
  productId: number;
  name: string;
  unit: string | null;
  category: string | null;
  quantity: number;
  decimals: number; // знаци след запетаята за мерната единица (decimal_places)
  minQty: number | null;
  wastePct: number | null;
  weightedAvgPrice: number | null;
  lastDeliveryPrice: number | null;
  vatRate: number;
  sumNoVat: number; // наличност × себестойност, без ДДС
  sumWithVat: number; // наличност × себестойност, с ДДС
  bucket: StockBucket;
  // Продуктът е изтрит в организацията (меко или физически — „#31"): чип
  // „изтрит", без минимум; липсва (undefined) при ред извън справката.
  deleted?: boolean;
  // Колко е продадено без наличност (знаковата наличност е под нулата) —
  // маркерът „−10 продадени без наличност" до нулата; 0/липсва — няма минус.
  soldWithoutStock?: number;
};

// Под минимума / отрицателно → under; до 20% над минимума → near; над 20% → over.
// Продукт БЕЗ зададен минимум не се следи: не влиза в „near" (и не надува KPI-то
// „почти свършващи") — само отрицателната наличност му е проблем.
export const bucketOf = (qty: number, min: number | null): StockBucket => {
  if (qty < 0) return "under";
  if (!min) return "over";
  if (qty < min) return "under";
  if (qty <= min * 1.2) return "near";
  return "over";
};

export const STOCK_BUCKETS: StockBucket[] = ["under", "near", "over"];

// Редът на един продукт (и за картата от „Продадени"). Продукт, който го няма
// в списъка (физически изтрит — „#31"), се строи само от реда на наличността.
export const stockRowOf = (
  product: Product | undefined,
  balance: StockBalance | undefined,
  vatRegistered: boolean,
): StockRow => {
  // Белегът на BE печели; без него (стар BE) продукт извън списъка е физически изтрит
  const deleted = isDeletedProduct(product) || (balance?.productDeleted ?? !product);
  // Наличността НИКОГА не се показва отрицателна — недостиг от разнесени
  // отчети се третира като 0 (физически няма под 0 на рафта; както при
  // инвентаризацията). Минусът стои до нулата като червен маркер, а по
  // отчети — в „Излишък от продажби".
  const quantity = shownStock(balance?.quantity ?? 0);
  // Изтритият продукт не се следи — минимумът му не вдига „под минимума"
  const minQty = deleted ? null : (product?.minQuantity ?? null);
  const unit = balance?.unit ?? product?.unit ?? null;
  // Стойност на наличността: себестойността е ВИНАГИ нето → Нето = к-во ×
  // цена; Бруто = нето × (1+ставка) при регистрирана по ДДС фирма, иначе
  // бруто = нето. Ср. цена (средно претеглената) се показва в БРУТО.
  const cost = balance?.weightedAvgPrice ?? balance?.lastDeliveryPrice ?? 0;
  const rate = product?.vatRate ?? 0;
  const grossFactor = vatRegistered && rate ? 1 + rate / 100 : 1;
  const sumNoVat = quantity * cost;
  return {
    productId: product?.id ?? balance?.productId ?? 0,
    name: product?.name ?? balance?.productName ?? "",
    unit,
    category: product?.category ?? null,
    quantity,
    decimals: clampDecimals(product?.decimalPlaces),
    minQty,
    wastePct: product?.wastePct ?? null,
    weightedAvgPrice:
      balance?.weightedAvgPrice != null
        ? balance.weightedAvgPrice * grossFactor
        : null,
    lastDeliveryPrice: balance?.lastDeliveryPrice ?? null,
    vatRate: rate,
    sumNoVat,
    sumWithVat: sumNoVat * grossFactor,
    bucket: bucketOf(quantity, minQty),
    deleted,
    soldWithoutStock: soldWithoutStockOf(balance?.quantity ?? 0),
  };
};

// Място в подредбата: с наличност → живите на нула → изтритите на нула.
const stockRank = (row: StockRow) => (row.quantity > 0 ? 0 : row.deleted ? 2 : 1);

// Ред за всеки ЖИВ продукт + за всеки изтрит, който BE връща в наличностите
// (с наличност или с история в склада) + за наличност на продукт извън списъка
// (физически изтрит — „#31"). Подредбата: първо продуктите С наличност, после
// нулевите, изтритите най-накрая — всяка група по азбучен ред (нулите са шум).
export const buildStockRows = (
  products: Product[],
  balanceByProduct: Map<number, StockBalance>,
  vatRegistered: boolean,
): StockRow[] => {
  const listed = new Set<number>();
  const rows: StockRow[] = [];
  for (const product of products) {
    listed.add(product.id);
    const balance = balanceByProduct.get(product.id);
    // Изтрит продукт без ред в наличностите няма нищо в този склад
    if (isDeletedProduct(product) && !balance) continue;
    rows.push(stockRowOf(product, balance, vatRegistered));
  }
  for (const [productId, balance] of balanceByProduct) {
    if (!listed.has(productId)) rows.push(stockRowOf(undefined, balance, vatRegistered));
  }
  return rows.sort(
    (a, b) => stockRank(a) - stockRank(b) || a.name.localeCompare(b.name, "bg"),
  );
};

// Групата на продукт за филтъра и колоната (без група → „—")
export const groupOf = (row: StockRow) => row.category || "—";

// ─── Филтри в колоните, търсене, сортиране ───────────────────────────────
// Група и Наличност (под минимума / до 20% / над) са във фуниите на
// колоните; продуктът се търси с търсачката над таблицата.
export type StockFilters = { groups: string[]; buckets: string[] };
export type StockFilterKey = keyof StockFilters;
export const EMPTY_STOCK_FILTERS: StockFilters = { groups: [], buckets: [] };

export const stockFilterSpecs = ({
  titles,
  bucketLabel,
}: {
  titles: Record<StockFilterKey, string>;
  bucketLabel: (bucket: string) => string;
}): FilterSpecs<StockRow, StockFilters> => ({
  groups: { kind: "list", title: titles.groups, valuesOf: (row) => [groupOf(row)] },
  // Подредени по смисъл (под → до 20% → над), не по азбука
  buckets: {
    kind: "list",
    title: titles.buckets,
    valuesOf: (row) => [row.bucket],
    labelOf: bucketLabel,
    order: STOCK_BUCKETS,
  },
});

export type StockSortKey =
  | "name"
  | "group"
  | "min"
  | "avgPrice"
  | "lastPrice"
  | "qty"
  | "net"
  | "gross";
export type StockSort = ReportSort<StockSortKey>;

// Текст и имена се подреждат от А, числата — от най-голямото
export const stockSortFirstDir = (key: StockSortKey) =>
  key === "name" || key === "group" ? "asc" : "desc";

// Търсене по всичко: име, категория, мерна единица, наличност, мин. и фира.
const matchesStockQuery = (row: StockRow, words: string[]) =>
  matchesWords(
    [
      row.name,
      row.category,
      row.unit,
      formatQty(row.quantity, row.decimals),
      row.minQty != null ? formatQty(row.minQty, row.decimals) : "",
      row.wastePct != null ? `${row.wastePct} ${row.wastePct}%` : "",
    ],
    words,
  );

const compareStock = (a: StockRow, b: StockRow, key: StockSortKey) => {
  switch (key) {
    case "name":
      return compareText(a.name, b.name);
    case "group":
      return compareText(groupOf(a), groupOf(b));
    case "min":
      return compareNumber(a.minQty, b.minQty);
    case "avgPrice":
      return compareNumber(a.weightedAvgPrice, b.weightedAvgPrice);
    case "lastPrice":
      return compareNumber(a.lastDeliveryPrice, b.lastDeliveryPrice);
    case "qty":
      return a.quantity - b.quantity;
    case "net":
      return a.sumNoVat - b.sumNoVat;
    case "gross":
      return a.sumWithVat - b.sumWithVat;
  }
};

// Базата за филтрите в колоните: търсенето и „Скрий без наличност" (само
// изглед — важи и за намереното, а броячите в попъпите го следват).
export const searchStockRows = (rows: StockRow[], query: string, hideZeroStock: boolean): StockRow[] => {
  const words = searchWords(query);
  if (!words.length && !hideZeroStock) return rows;
  return rows.filter((row) => (!hideZeroStock || row.quantity > 0) && matchesStockQuery(row, words));
};

// Без избрано сортиране остава подредбата на buildStockRows
export const sortStockRows = (rows: StockRow[], sort: StockSort | null) => sortRows(rows, sort, compareStock);

// ─── KPI и суми ──────────────────────────────────────────────────────────
export const stockTotals = (rows: StockRow[]) => ({
  net: rows.reduce((sum, row) => sum + row.sumNoVat, 0),
  gross: rows.reduce((sum, row) => sum + row.sumWithVat, 0),
});

// Подравняването на количествата по запетаята: ширината (в знаци) на дробната
// зона — „,000" + запетаята — и на мерната единица. Смята се за ЦЕЛИЯ филтриран
// резултат (не само видимите преди „Покажи още"), за да не скача при
// „Покажи още". Мерната единица над UNIT_MAX_CHARS се съкращава (с подсказка).
const UNIT_MAX_CHARS = 8;
export type StockQtyLayout = { fractionChars: number; unitChars: number };

export const stockQtyLayout = (rows: StockRow[]): StockQtyLayout => {
  let fractionChars = 0;
  let unitChars = 0;
  for (const row of rows) {
    if (!row.quantity && !row.minQty) continue; // клетката е „—"
    const decimals = clampDecimals(row.decimals);
    fractionChars = Math.max(fractionChars, decimals ? decimals + 1 : 0);
    unitChars = Math.max(unitChars, Math.min(UNIT_MAX_CHARS, row.unit?.length ?? 0));
  }
  return { fractionChars, unitChars };
};

// KPI-тата са за ЦЕЛИЯ склад (снимката), не за филтрираното — както KPI-тата
// на „Доставки" са за периода; лентата над таблицата показва филтрираното.
export const stockKpis = (rows: StockRow[]) => {
  const counts = { under: 0, near: 0, over: 0 };
  for (const row of rows) counts[row.bucket]++;
  return {
    total: rows.length,
    inStock: rows.filter((row) => row.quantity > 0).length,
    ...counts,
    ...stockTotals(rows),
  };
};
